// services/wrapped/synth.ts
// Sintetizador próprio do LibriTui Wrapped: música e efeitos sonoros gerados 100% por código.
// Sem amostras de terceiros, portanto sem copyright de ninguém além do próprio LibriTui.
// Funções puras (sem Web Audio): rodam no navegador (reserva) e no Node (scripts/wrapped/build-audio.mjs).
// Obs.: só sintaxe TypeScript "apagável", para o Node conseguir executar este arquivo diretamente.

export const SAMPLE_RATE = 44100;

export interface StereoBuffer {
  left: Float32Array;
  right: Float32Array;
  sampleRate: number;
}

const TAU = Math.PI * 2;
const mtof = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

const makeRng = (seed: number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const stereo = (seconds: number, sr = SAMPLE_RATE): StereoBuffer => {
  const n = Math.ceil(seconds * sr);
  return { left: new Float32Array(n), right: new Float32Array(n), sampleRate: sr };
};

// ── Filtro biquad (RBJ) ───────────────────────────────────────────
type FilterType = 'lowpass' | 'highpass' | 'bandpass';

const makeBiquad = (sr: number) => {
  let b0 = 1, b1 = 0, b2 = 0, a1 = 0, a2 = 0;
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  return {
    set(type: FilterType, freq: number, q = 0.707) {
      const f = Math.min(Math.max(freq, 20), sr * 0.45);
      const w0 = (TAU * f) / sr;
      const cos = Math.cos(w0);
      const alpha = Math.sin(w0) / (2 * q);
      const a0 = 1 + alpha;
      let nb0 = 0, nb1 = 0, nb2 = 0;
      if (type === 'lowpass') { nb0 = (1 - cos) / 2; nb1 = 1 - cos; nb2 = (1 - cos) / 2; }
      else if (type === 'highpass') { nb0 = (1 + cos) / 2; nb1 = -(1 + cos); nb2 = (1 + cos) / 2; }
      else { nb0 = alpha; nb1 = 0; nb2 = -alpha; }
      b0 = nb0 / a0; b1 = nb1 / a0; b2 = nb2 / a0; a1 = (-2 * cos) / a0; a2 = (1 - alpha) / a0;
    },
    process(x: number) {
      const y = b0 * x + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
      x2 = x1; x1 = x; y2 = y1; y1 = y;
      return y;
    },
  };
};

const filterInPlace = (buf: Float32Array, type: FilterType, freq: number, q = 0.707, sr = SAMPLE_RATE) => {
  const f = makeBiquad(sr);
  f.set(type, freq, q);
  for (let i = 0; i < buf.length; i++) buf[i] = f.process(buf[i]);
};

// ── Reverb (Schroeder leve): mono -> estéreo ──────────────────────
const reverb = (input: Float32Array, out: StereoBuffer, wet: number, size = 0.8) => {
  const combDelays = [1557, 1617, 1491, 1422, 1277, 1356];
  const allDelays = [225, 556, 441];
  const makeChannel = (offset: number) => {
    const combs = combDelays.map(d => ({ buf: new Float32Array(d + offset), idx: 0, last: 0 }));
    const alls = allDelays.map(d => ({ buf: new Float32Array(d + Math.round(offset / 2)), idx: 0 }));
    return (x: number) => {
      let s = 0;
      for (const c of combs) {
        const y = c.buf[c.idx];
        c.last = y * 0.6 + c.last * 0.4; // amortecimento (agudos somem nas reflexões)
        c.buf[c.idx] = x + c.last * size;
        c.idx = (c.idx + 1) % c.buf.length;
        s += y;
      }
      s /= combs.length;
      for (const a of alls) {
        const b = a.buf[a.idx];
        const y = -s + b;
        a.buf[a.idx] = s + b * 0.5;
        a.idx = (a.idx + 1) % a.buf.length;
        s = y;
      }
      return s;
    };
  };
  const l = makeChannel(0);
  const r = makeChannel(23);
  for (let i = 0; i < out.left.length; i++) {
    const x = i < input.length ? input[i] : 0;
    out.left[i] += l(x) * wet;
    out.right[i] += r(x) * wet;
  }
};

// ── Osciladores e envelopes ───────────────────────────────────────
const tri = (p: number) => 2 * Math.abs(2 * (p - Math.floor(p + 0.5))) - 1;

const adsr = (t: number, dur: number, a: number, r: number) => {
  if (t < 0) return 0;
  if (t < a) return t / a;
  if (t < dur) return 1;
  const k = 1 - (t - dur) / r;
  return k > 0 ? k : 0;
};

const addPan = (out: StereoBuffer, i: number, v: number, pan: number) => {
  // pan -1..1 (lei de potência constante)
  const a = ((pan + 1) * Math.PI) / 4;
  out.left[i] += v * Math.cos(a);
  out.right[i] += v * Math.sin(a);
};

// Pad: 3 vozes levemente desafinadas, ataque e saída longos
const pad = (bus: Float32Array, midi: number, start: number, dur: number, amp: number, sr = SAMPLE_RATE) => {
  const rel = 1.4, att = 0.9;
  const s0 = Math.floor(start * sr);
  const n = Math.floor((dur + rel) * sr);
  const detunes = [-0.07, 0, 0.065];
  for (const dt of detunes) {
    const f = mtof(midi + dt);
    let p = Math.random();
    for (let k = 0; k < n && s0 + k < bus.length; k++) {
      const t = k / sr;
      p += f / sr;
      bus[s0 + k] += tri(p) * adsr(t, dur, att, rel) * amp / detunes.length;
    }
  }
};

// Teclas suaves (felt piano): fundamental + harmônicos com decaimento exponencial
const keys = (bus: Float32Array, midi: number, start: number, amp: number, decay = 1.1, sr = SAMPLE_RATE) => {
  const f = mtof(midi);
  const s0 = Math.floor(start * sr);
  const n = Math.floor(decay * 4 * sr);
  for (let k = 0; k < n && s0 + k < bus.length; k++) {
    const t = k / sr;
    const env = Math.min(1, t / 0.004) * Math.exp(-t / decay);
    const v = Math.sin(TAU * f * t) + 0.32 * Math.sin(TAU * 2.001 * f * t) * Math.exp(-t / (decay * 0.5)) + 0.1 * Math.sin(TAU * 3.003 * f * t) * Math.exp(-t / (decay * 0.3));
    bus[s0 + k] += v * env * amp;
  }
};

// Pluck: dente de serra aditivo com decaimento rápido (vai para um passa-baixa no bus)
const pluck = (bus: Float32Array, midi: number, start: number, amp: number, decay = 0.28, sr = SAMPLE_RATE) => {
  const f = mtof(midi);
  const s0 = Math.floor(start * sr);
  const n = Math.floor(decay * 5 * sr);
  for (let k = 0; k < n && s0 + k < bus.length; k++) {
    const t = k / sr;
    const env = Math.min(1, t / 0.003) * Math.exp(-t / decay);
    let v = 0;
    for (let h = 1; h <= 7; h++) v += Math.sin(TAU * f * h * t) * (1 / h) * Math.exp(-t * h * 2.2);
    bus[s0 + k] += v * env * amp;
  }
};

const bass = (bus: Float32Array, midi: number, start: number, dur: number, amp: number, sr = SAMPLE_RATE) => {
  const f = mtof(midi);
  const s0 = Math.floor(start * sr);
  const n = Math.floor((dur + 0.08) * sr);
  let p = 0;
  for (let k = 0; k < n && s0 + k < bus.length; k++) {
    const t = k / sr;
    p += f / sr;
    const env = adsr(t, dur, 0.012, 0.08) * (0.75 + 0.25 * Math.exp(-t * 6));
    bus[s0 + k] += (Math.sin(TAU * p) + 0.25 * tri(p)) * env * amp;
  }
};

const kick = (out: StereoBuffer, start: number, amp: number) => {
  const sr = out.sampleRate;
  const s0 = Math.floor(start * sr);
  const n = Math.floor(0.45 * sr);
  let p = 0;
  for (let k = 0; k < n && s0 + k < out.left.length; k++) {
    const t = k / sr;
    const f = 42 + 110 * Math.exp(-t * 28);
    p += f / sr;
    const v = Math.sin(TAU * p) * Math.exp(-t * 7.5) * amp + (k < 60 ? (Math.random() * 2 - 1) * 0.15 * amp * (1 - k / 60) : 0);
    out.left[s0 + k] += v;
    out.right[s0 + k] += v;
  }
};

const noiseHit = (out: StereoBuffer, start: number, amp: number, opts: { type: FilterType; freq: number; q?: number; decay: number; len: number; pan?: number; bursts?: number[] }, rnd: () => number) => {
  const sr = out.sampleRate;
  const s0 = Math.floor(start * sr);
  const n = Math.floor(opts.len * sr);
  const f = makeBiquad(sr);
  f.set(opts.type, opts.freq, opts.q ?? 0.9);
  const bursts = opts.bursts || [0];
  for (let k = 0; k < n && s0 + k < out.left.length; k++) {
    const t = k / sr;
    let env = 0;
    for (const b of bursts) if (t >= b) env = Math.max(env, Math.exp(-(t - b) / opts.decay));
    const v = f.process(rnd() * 2 - 1) * env * amp;
    addPan(out, s0 + k, v, opts.pan ?? 0);
  }
};

const softClip = (x: number) => Math.tanh(x);

const finalize = (out: StereoBuffer, gain: number, fadeIn: number, fadeOut: number) => {
  const sr = out.sampleRate;
  const n = out.left.length;
  let peak = 0;
  for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(out.left[i]), Math.abs(out.right[i]));
  const norm = peak > 0 ? gain / peak : 1;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const fi = fadeIn > 0 ? Math.min(1, t / fadeIn) : 1;
    const fo = fadeOut > 0 ? Math.min(1, (n / sr - t) / fadeOut) : 1;
    const env = fi * Math.max(0, fo);
    out.left[i] = softClip(out.left[i] * norm * 1.1) * env * 0.92;
    out.right[i] = softClip(out.right[i] * norm * 1.1) * env * 0.92;
  }
  return out;
};

const mixMono = (out: StereoBuffer, bus: Float32Array, gain: number) => {
  for (let i = 0; i < out.left.length && i < bus.length; i++) {
    out.left[i] += bus[i] * gain;
    out.right[i] += bus[i] * gain;
  }
};

// ── Músicas ───────────────────────────────────────────────────────
export type MusicStyle = 'soft' | 'upbeat';

/** Trilha de ~34 s. "soft": lo-fi calmo (84 bpm). "upbeat": pop leve e animado (112 bpm). */
export const renderMusic = (style: MusicStyle, seconds = 34, sr = SAMPLE_RATE): StereoBuffer => {
  const rnd = makeRng(style === 'soft' ? 7 : 11);
  const out = stereo(seconds, sr);
  const n = out.left.length;
  const padBus = new Float32Array(n);
  const keyBus = new Float32Array(n);
  const plkBus = new Float32Array(n);
  const bassBus = new Float32Array(n);

  if (style === 'soft') {
    const bpm = 84, beat = 60 / bpm, bar = beat * 4;
    // Cmaj9 - Am9 - Fmaj7(#11) - G6/9 (um compasso cada)
    const chords = [
      [48, 55, 59, 62, 64], [45, 52, 55, 59, 60], [41, 48, 52, 55, 57], [43, 50, 52, 57, 59],
    ];
    const arp = [0, 2, 4, 3, 1, 3, 4, 2];
    for (let b = 0; b * bar < seconds; b++) {
      const ch = chords[b % chords.length];
      const t0 = b * bar;
      for (let v = 1; v < ch.length; v++) pad(padBus, ch[v], t0, bar, 0.16);
      bass(bassBus, ch[0] - 12, t0, bar * 0.9, 0.38);
      if (b >= 1) {
        for (let s = 0; s < 8; s++) {
          if (rnd() < 0.18) continue; // respiros no arpejo
          keys(keyBus, ch[arp[s] % ch.length] + 12, t0 + s * (beat / 2) + rnd() * 0.012, 0.13 + rnd() * 0.05, 1.0);
        }
      }
      if (b >= 2) {
        kick(out, t0, 0.5);
        kick(out, t0 + beat * 2.5, 0.38);
        for (let s = 0; s < 8; s++) {
          if (s % 2 === 1) noiseHit(out, t0 + s * (beat / 2), 0.05 + rnd() * 0.02, { type: 'highpass', freq: 7500, decay: 0.018, len: 0.07, pan: rnd() * 0.6 - 0.3 }, rnd);
        }
        noiseHit(out, t0 + beat, 0.11, { type: 'bandpass', freq: 1800, q: 0.7, decay: 0.07, len: 0.3, bursts: [0, 0.011] }, rnd);
        noiseHit(out, t0 + beat * 3, 0.11, { type: 'bandpass', freq: 1800, q: 0.7, decay: 0.07, len: 0.3, bursts: [0, 0.011] }, rnd);
      }
    }
    // chiado de vinil bem sutil
    for (let i = 0; i < n; i++) if (rnd() < 0.00025) { const v = (rnd() * 2 - 1) * 0.06; out.left[i] += v; out.right[i] += v * 0.8; }
    filterInPlace(padBus, 'lowpass', 1600);
    filterInPlace(bassBus, 'lowpass', 400);
    mixMono(out, padBus, 0.55);
    mixMono(out, keyBus, 0.75);
    mixMono(out, bassBus, 0.6);
    reverb(padBus, out, 0.35, 0.82);
    reverb(keyBus, out, 0.45, 0.8);
    return finalize(out, 0.85, 1.2, 2.5);
  }

  // upbeat
  const bpm = 112, beat = 60 / bpm, bar = beat * 4;
  // Am - F - C - G
  const chords = [
    [45, 57, 60, 64], [41, 57, 60, 65], [48, 55, 60, 64], [43, 55, 59, 62],
  ];
  const pattern = [0, 3, 2, 3, 1, 3, 2, 3, 0, 3, 2, 3, 1, 2, 3, 2];
  for (let b = 0; b * bar < seconds; b++) {
    const ch = chords[b % chords.length];
    const t0 = b * bar;
    for (let v = 1; v < ch.length; v++) pad(padBus, ch[v], t0, bar, 0.1);
    const intro = b < 1;
    for (let s = 0; s < 16; s++) {
      const t = t0 + s * (beat / 4);
      pluck(plkBus, ch[pattern[s]] + 12, t, (s % 4 === 0 ? 0.2 : 0.13) * (intro ? 0.7 : 1), 0.2);
    }
    if (!intro) {
      for (let q = 0; q < 8; q++) bass(bassBus, ch[0] - 12 + (q === 7 ? 7 : 0), t0 + q * (beat / 2), beat / 2 * 0.8, 0.42);
      for (let q = 0; q < 4; q++) kick(out, t0 + q * beat, 0.62);
      noiseHit(out, t0 + beat, 0.2, { type: 'bandpass', freq: 1500, q: 0.8, decay: 0.09, len: 0.35, bursts: [0, 0.009, 0.019] }, rnd);
      noiseHit(out, t0 + beat * 3, 0.2, { type: 'bandpass', freq: 1500, q: 0.8, decay: 0.09, len: 0.35, bursts: [0, 0.009, 0.019] }, rnd);
      for (let s = 0; s < 16; s++) {
        noiseHit(out, t0 + s * (beat / 4), s % 2 ? 0.035 : 0.06, { type: 'highpass', freq: 8000, decay: s % 4 === 2 ? 0.05 : 0.015, len: 0.12, pan: s % 2 ? 0.25 : -0.25 }, rnd);
      }
    }
  }
  filterInPlace(padBus, 'lowpass', 2200);
  filterInPlace(plkBus, 'lowpass', 3400);
  filterInPlace(bassBus, 'lowpass', 500);
  mixMono(out, padBus, 0.45);
  mixMono(out, plkBus, 0.6);
  mixMono(out, bassBus, 0.55);
  reverb(plkBus, out, 0.3, 0.78);
  reverb(padBus, out, 0.3, 0.8);
  return finalize(out, 0.85, 0.6, 2.5);
};

// ── Efeitos sonoros ───────────────────────────────────────────────
export type SfxName = 'whoosh' | 'pop' | 'tick' | 'chime' | 'rise' | 'sparkle';

export const renderSfx = (name: SfxName, sr = SAMPLE_RATE): StereoBuffer => {
  const rnd = makeRng(name.length * 97 + 3);
  if (name === 'whoosh') {
    // ruído num passa-banda que varre de grave para agudo, cruzando da esquerda para a direita
    const len = 0.7;
    const out = stereo(len, sr);
    const f = makeBiquad(sr);
    for (let i = 0; i < out.left.length; i++) {
      const t = i / sr;
      const p = t / len;
      if (i % 32 === 0) f.set('bandpass', 300 + 4200 * Math.pow(p, 1.6), 1.4);
      const env = Math.sin(Math.PI * Math.min(1, p * 1.15)) ** 2;
      addPan(out, i, f.process(rnd() * 2 - 1) * env * 0.9, -0.7 + 1.4 * p);
    }
    return finalize(out, 0.55, 0, 0.05);
  }
  if (name === 'pop') {
    const len = 0.16;
    const out = stereo(len, sr);
    let ph = 0;
    for (let i = 0; i < out.left.length; i++) {
      const t = i / sr;
      ph += (520 + 600 * (1 - Math.exp(-t * 60))) / sr;
      const v = Math.sin(TAU * ph) * Math.exp(-t * 32) + (i < 40 ? (rnd() * 2 - 1) * 0.3 * (1 - i / 40) : 0);
      out.left[i] = v; out.right[i] = v;
    }
    return finalize(out, 0.6, 0, 0.01);
  }
  if (name === 'tick') {
    const len = 0.05;
    const out = stereo(len, sr);
    for (let i = 0; i < out.left.length; i++) {
      const t = i / sr;
      const v = Math.sin(TAU * 2350 * t) * Math.exp(-t * 120);
      out.left[i] = v; out.right[i] = v;
    }
    return finalize(out, 0.35, 0, 0.005);
  }
  if (name === 'chime') {
    // sino: parciais inarmônicos com decaimentos diferentes (Mi6 + Si6)
    const len = 2.4;
    const out = stereo(len, sr);
    const notes = [{ f: mtof(88), d: 0 }, { f: mtof(95), d: 0.09 }];
    const partials = [[1, 1, 1.4], [2.76, 0.45, 0.7], [5.4, 0.22, 0.35], [8.93, 0.1, 0.2]];
    for (const nt of notes) {
      const s0 = Math.floor(nt.d * sr);
      for (let k = 0; s0 + k < out.left.length; k++) {
        const t = k / sr;
        let v = 0;
        for (const [ratio, a, dec] of partials) v += Math.sin(TAU * nt.f * ratio * t) * a * Math.exp(-t / dec);
        v *= Math.min(1, t / 0.002) * 0.5;
        out.left[s0 + k] += v * (nt.d ? 0.8 : 1);
        out.right[s0 + k] += v * (nt.d ? 1 : 0.8);
      }
    }
    const mono = new Float32Array(out.left.length);
    for (let i = 0; i < mono.length; i++) mono[i] = (out.left[i] + out.right[i]) * 0.5;
    reverb(mono, out, 0.5, 0.82);
    return finalize(out, 0.55, 0, 0.3);
  }
  if (name === 'rise') {
    const len = 1.6;
    const out = stereo(len, sr);
    const f = makeBiquad(sr);
    let ph = 0;
    for (let i = 0; i < out.left.length; i++) {
      const t = i / sr;
      const p = t / len;
      if (i % 32 === 0) f.set('bandpass', 400 + 6000 * p * p, 2);
      ph += (220 + 660 * p * p) / sr;
      const env = Math.pow(p, 2.2) * (p < 0.97 ? 1 : (1 - p) / 0.03);
      const v = (f.process(rnd() * 2 - 1) * 0.7 + Math.sin(TAU * ph) * 0.25) * env;
      out.left[i] = v; out.right[i] = v;
    }
    return finalize(out, 0.5, 0, 0.02);
  }
  // sparkle: brilhos agudos espalhados
  const len = 1.4;
  const out = stereo(len, sr);
  const scale = [88, 91, 93, 95, 98, 100, 103];
  for (let j = 0; j < 9; j++) {
    const start = j * 0.07 + rnd() * 0.04;
    const fr = mtof(scale[Math.floor(rnd() * scale.length)]);
    const pan = rnd() * 1.6 - 0.8;
    const s0 = Math.floor(start * sr);
    for (let k = 0; k < sr * 0.6 && s0 + k < out.left.length; k++) {
      const t = k / sr;
      const v = (Math.sin(TAU * fr * t) + 0.3 * Math.sin(TAU * fr * 2.76 * t) * Math.exp(-t * 18)) * Math.exp(-t * 9) * 0.35 * (1 - j / 12);
      addPan(out, s0 + k, v, pan);
    }
  }
  const mono = new Float32Array(out.left.length);
  for (let i = 0; i < mono.length; i++) mono[i] = (out.left[i] + out.right[i]) * 0.5;
  reverb(mono, out, 0.4, 0.75);
  return finalize(out, 0.5, 0, 0.2);
};

// ── WAV (PCM 16 bits) ─────────────────────────────────────────────
export const encodeWav = (buf: StereoBuffer): Uint8Array => {
  const n = buf.left.length;
  const bytes = new Uint8Array(44 + n * 4);
  const dv = new DataView(bytes.buffer);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) bytes[o + i] = s.charCodeAt(i); };
  str(0, 'RIFF'); dv.setUint32(4, 36 + n * 4, true); str(8, 'WAVE');
  str(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 2, true);
  dv.setUint32(24, buf.sampleRate, true); dv.setUint32(28, buf.sampleRate * 4, true); dv.setUint16(32, 4, true); dv.setUint16(34, 16, true);
  str(36, 'data'); dv.setUint32(40, n * 4, true);
  let o = 44;
  for (let i = 0; i < n; i++) {
    dv.setInt16(o, Math.max(-1, Math.min(1, buf.left[i])) * 32767, true); o += 2;
    dv.setInt16(o, Math.max(-1, Math.min(1, buf.right[i])) * 32767, true); o += 2;
  }
  return bytes;
};
